import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { RootState } from '../store';

// Caches the full getHomeContent response body:
// { data: briefing[], pagination, podcasts, columns, ... }
// `cachedForEmail` guards against serving one user's content to another
// (the API returns different content for logged-out / trial / paid users).
interface HomeState {
  data: any | null;
  status: 'idle' | 'loading' | 'failed';
  cachedForEmail: string;
  currentPage: number;
  lastFetched: number | null;
}

const initialState: HomeState = {
  data: null,
  status: 'idle',
  cachedForEmail: '',
  currentPage: 1,
  lastFetched: null,
};

const dedupeById = (items: any[]) => {
  const seen = new Set();
  return items.filter((item) => {
    const id = item?.id;
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const homeReducer = createSlice({
  name: 'home',
  initialState,
  reducers: {
    setHomeLoading: (state) => {
      state.status = 'loading';
    },
    setHomeFailed: (state) => {
      state.status = 'failed';
    },
    // Replace the cache with a fresh page-1 response.
    setHomeData: (
      state,
      action: PayloadAction<{ body: any; email: string; page?: number }>
    ) => {
      state.data = action.payload.body;
      state.cachedForEmail = action.payload.email;
      state.currentPage = action.payload.page ?? 1;
      state.status = 'idle';
      state.lastFetched = Date.now();
    },
    // Append a further page of briefings; pagination/podcasts/columns come
    // from the latest response (same merge the previous hook performed).
    appendHomeData: (
      state,
      action: PayloadAction<{ body: any; page: number }>
    ) => {
      const newBody = action.payload.body;
      state.data = {
        ...newBody,
        data: dedupeById([
          ...(state.data?.data ?? []),
          ...(newBody?.data ?? []),
        ]),
      };
      state.currentPage = action.payload.page;
      state.status = 'idle';
      state.lastFetched = Date.now();
    },
    // Cache is valid but a background check found nothing new.
    touchHomeData: (state) => {
      state.lastFetched = Date.now();
      state.status = 'idle';
    },
    clearHome: () => initialState,
  },
});

export const {
  setHomeLoading,
  setHomeFailed,
  setHomeData,
  appendHomeData,
  touchHomeData,
  clearHome,
} = homeReducer.actions;

export const selectHome = (state: RootState) => state.home;
export const selectHomeData = (state: RootState) => state.home.data;

export default homeReducer.reducer;
